/**
 * Componente de Registro/Edición de Pacientes (RegistrarPaciente).
 * Gestiona el formulario de datos demográficos, ubicación y vinculación a proyectos.
 */

import React from "react";
import { useRegistrarPaciente } from "../hooks/useRegistrarPaciente";

const RegistrarPaciente = ({ showToast, setView, initialData, isEditMode, onCancel, setSelectedPaciente, activeProjectId, projects = [] }) => {
    const activeProject = projects.find(p => p.id === activeProjectId);

    const {
        municipio, zona, lugar, setLugar, coords, fechaNacimiento, fechaEvento, setFechaEvento, edad, municipiosList,
        showConfirm, setShowConfirm, confirmData, showClassification, createdPatient,
        loadingClassification, patientExistsInProject, setCreatedPatient, setShowClassification,
        handleMunicipioChange, handleZonaChange, handleFechaChange, handlePreSubmit, handleFinalSubmit, handleClassification
    } = useRegistrarPaciente(initialData, isEditMode, activeProjectId, activeProject, showToast, setView, setSelectedPaciente, onCancel);

    const onSubmit = (e) => {
        e.preventDefault();
        const formData = new FormData(e.target);
        handlePreSubmit(Object.fromEntries(formData.entries()));
    };

    return (
        <div className="animate__animated animate__fadeIn position-relative">
            {/* Cabecera del Formulario */}
            <div className="mb-2 d-flex justify-content-between align-items-end border-bottom pb-1">
                <div>
                    <h6 className="fw-bold text-dark m-0">{isEditMode ? "Modificar Ficha de Sujeto ID:" : "Registro de Nuevo Sujeto de Estudio"}</h6>
                    <p className="text-muted smaller mb-0">Diligencie los datos demográficos y geográficos según el protocolo</p>
                </div>
                {isEditMode && (
                    <button className="btn btn-outline-secondary border-2 fw-bold px-3 py-1 mb-1" style={{ borderRadius: '8px', fontSize: '0.8rem' }} onClick={onCancel}>
                        ABORTAR EDICIÓN
                    </button>
                )}
            </div>

            <form id="regPacienteInvestigadorForm" onSubmit={onSubmit}>
                <div className="card border-0 shadow-sm mb-3" style={{ borderRadius: '16px' }}>
                    <div className="card-body p-3">
                        <div className="row g-2">
                            {/* Sección 1: Identificación Científica */}
                            <div className="col-12">
                                <div className="d-flex align-items-center gap-2 mb-1">
                                    <div className="bg-success bg-opacity-10 p-1 rounded">
                                        <i className="bi bi-person-lines-fill text-success" style={{ fontSize: '0.8rem' }}></i>
                                    </div>
                                    <h6 className="m-0 fw-bold text-muted text-uppercase smaller" style={{ letterSpacing: '0.5px', fontSize: '0.65rem' }}>Identificación del Sujeto</h6>
                                </div>
                            </div>

                            <div className="col-md-6">
                                <label className="form-label smaller fw-bold text-muted mb-1">NOMBRE COMPLETO</label>
                                <input name="nombre" className="form-control form-control-sm border-2" style={{ borderRadius: '8px' }} defaultValue={initialData?.nombre} required placeholder="Ej: Juan Pérez..." />
                            </div>

                            <div className="col-md-3">
                                <label className="form-label smaller fw-bold text-muted mb-1">DOCUMENTO</label>
                                <input name="documento" className="form-control form-control-sm border-2 font-monospace" style={{ borderRadius: '8px' }} defaultValue={initialData?.documento} required placeholder="CC / TI / RC" />
                            </div>

                            <div className="col-md-3">
                                <label className="form-label smaller fw-bold text-muted mb-1">GÉNERO</label>
                                <select name="sexo" className="form-select form-select-sm border-2" style={{ borderRadius: '8px' }} defaultValue={initialData?.sexo} required>
                                    <option value="">Seleccione...</option>
                                    <option value="M">Masculino</option>
                                    <option value="F">Femenino</option>
                                </select>
                            </div>

                            {/* Sección 2: Protocolo de Investigación */}
                            <div className="col-12 mt-2">
                                <div className="d-flex align-items-center gap-2 mb-1">
                                    <div className="bg-primary bg-opacity-10 p-1 rounded">
                                        <i className="bi bi-folder2-open text-primary" style={{ fontSize: '0.8rem' }}></i>
                                    </div>
                                    <h6 className="m-0 fw-bold text-muted text-uppercase smaller" style={{ letterSpacing: '0.5px', fontSize: '0.65rem' }}>Vínculo con Proyecto Basal</h6>
                                </div>
                            </div>

                            <div className="col-md-12">
                                <div className="p-2 border-2 border border-primary border-opacity-25 bg-primary bg-opacity-10 d-flex align-items-center justify-content-between" style={{ borderRadius: '10px' }}>
                                    <label className="fw-bold smaller text-primary mb-0">PROYECTO ACTIVO:</label>
                                    <div className="d-flex align-items-center gap-2">
                                        <i className="bi bi-shield-check text-primary fs-6"></i>
                                        <div className="fw-bold text-dark small">
                                            {activeProject ? (
                                                <>
                                                    {activeProject.nombre}
                                                    <span className="badge bg-primary ms-2 font-monospace" style={{ fontSize: '0.7rem' }}>{activeProject.codigo}</span>
                                                </>
                                            ) : (
                                                <span className="text-danger small">⚠️ NINGÚN PROYECTO SELECCIONADO</span>
                                            )}
                                        </div>
                                    </div>
                                </div>
                            </div>

                            {/* Sección 3: Datos Biométricos y de Ubicación */}
                            <div className="col-12 mt-2">
                                <div className="d-flex align-items-center gap-2 mb-1">
                                    <div className="bg-warning bg-opacity-10 p-1 rounded">
                                        <i className="bi bi-geo-alt-fill text-warning" style={{ fontSize: '0.8rem' }}></i>
                                    </div>
                                    <h6 className="m-0 fw-bold text-muted text-uppercase smaller" style={{ letterSpacing: '0.5px', fontSize: '0.65rem' }}>Variables Geográficas y Biométricas</h6>
                                </div>
                            </div>

                            <div className="col-md-3">
                                <label className="form-label smaller fw-bold text-muted mb-1">MUNICIPIO</label>
                                <select name="municipio_select" className="form-select form-select-sm border-2" style={{ borderRadius: '8px' }} value={municipio} onChange={(e) => handleMunicipioChange(e.target.value)} required>
                                    <option value="">Seleccione...</option>
                                    {municipiosList.map(m => <option key={m} value={m}>{m}</option>)}
                                </select>
                            </div>

                            <div className="col-md-3">
                                <label className="form-label smaller fw-bold text-muted mb-1">ZONA</label>
                                <select
                                    name="zona_select"
                                    className="form-select form-select-sm border-2"
                                    style={{ borderRadius: '8px' }}
                                    value={zona}
                                    onChange={(e) => handleZonaChange(e.target.value)}
                                    required
                                >
                                    <option value="">Seleccione...</option>
                                    <option value="Urbana">Urbana</option>
                                    <option value="Rural">Rural</option>
                                </select>
                            </div>

                            <div className="col-md-3">
                                <label className="form-label smaller fw-bold text-muted mb-1">F. NACIMIENTO / EDAD</label>
                                <div className="input-group input-group-sm">
                                    <input name="fecha_nacimiento" type="date" className="form-control border-2" style={{ borderRadius: '8px 0 0 8px' }} value={fechaNacimiento} onChange={(e) => handleFechaChange(e.target.value)} required />
                                    <span className="input-group-text bg-light border-2 border-start-0 fw-bold font-monospace" style={{ borderRadius: '0 8px 8px 0', fontSize: '0.7rem' }}>
                                        {edad || "0"}A
                                    </span>
                                </div>
                            </div>

                            <div className="col-md-3">
                                <label className="form-label smaller fw-bold text-muted mb-1">FECHA DEL EVENTO</label>
                                <input
                                    name="fecha_evento_input"
                                    type="date"
                                    className="form-control form-control-sm border-2"
                                    style={{ borderRadius: '8px' }}
                                    value={fechaEvento}
                                    onChange={(e) => setFechaEvento(e.target.value)}
                                    required
                                />
                            </div>

                            <div className="col-md-8">
                                <label className="form-label smaller fw-bold text-muted mb-1">DIRECCIÓN / VEREDA / DESCRIPCIÓN LOCAL</label>
                                <input name="direccion_detalles" className="form-control form-control-sm border-2" style={{ borderRadius: '8px' }} placeholder="Calle, Carrera, descripción detallada..." />
                            </div>

                            <div className="col-md-4">
                                <label className="form-label smaller fw-bold text-muted mb-1">GEOPOSICIONAMIENTO (AUTO)</label>
                                <div className="p-1 bg-light border-2 border rounded font-monospace smaller text-center d-flex align-items-center justify-content-center" style={{ minHeight: '35px', fontSize: '0.7rem' }}>
                                    <i className="bi bi-pin-map-fill text-danger me-2"></i>
                                    {coords.lat && coords.lng ? `${coords.lat}, ${coords.lng}` : "PENDIENTE"}
                                </div>
                            </div>

                            <div className="col-md-4">
                                <label className="form-label smaller fw-bold text-muted mb-1">TELÉFONO DE CONTACTO</label>
                                <input name="telefono" className="form-control form-control-sm border-2" style={{ borderRadius: '8px' }} defaultValue={initialData?.telefono} placeholder="Ejem: 310..." />
                            </div>
                        </div>
                    </div>

                    <div className="card-footer bg-white border-0 p-3 pt-0 text-end">
                        <div className="d-flex justify-content-end gap-2 mt-2 mt-md-0">
                            <button type="button" className="btn btn-light btn-sm fw-bold px-3 py-2" style={{ borderRadius: '10px' }} onClick={onCancel || (() => setView("home"))}>DESCARTE DE DATOS</button>
                            <button type="submit" className="btn btn-success btn-sm fw-bold px-4 py-2 shadow-sm d-flex align-items-center gap-2" style={{ borderRadius: '10px' }}>
                                <i className="bi bi-cloud-arrow-up-fill"></i>
                                {isEditMode ? "ACTUALIZAR" : "VINCULAR"}
                            </button>
                        </div>
                    </div>
                </div>
            </form>

            {/* Modal de Confirmación Premium */}
            {showConfirm && confirmData && (
                <div className="position-absolute top-0 start-0 w-100 h-100 d-flex align-items-center justify-content-center p-3" style={{ zIndex: 1100, background: 'rgba(255,255,255,0.85)', backdropFilter: 'blur(10px)', borderRadius: '30px' }}>
                    <div className={`card shadow-lg border-0 animate__animated animate__zoomIn ${patientExistsInProject ? 'bg-warning bg-opacity-10' : 'bg-success bg-opacity-10'}`} style={{ maxWidth: '500px', width: '100%', borderRadius: '24px' }}>
                        <div className="card-body p-5 text-center">
                            <div className={`mx-auto mb-4 p-3 rounded-circle d-flex align-items-center justify-content-center ${patientExistsInProject ? 'bg-warning' : 'bg-success'}`} style={{ width: '80px', height: '80px' }}>
                                <i className={`bi ${patientExistsInProject ? 'bi-exclamation-triangle' : 'bi-check-lg'} text-white fs-1`}></i>
                            </div>

                            {patientExistsInProject ? (
                                <>
                                    <h4 className="fw-bold text-dark">Sujeto Ya Vinculado</h4>
                                    <p className="text-muted">El documento <strong>{confirmData.documento}</strong> ya forma parte del protocolo <strong>{activeProject?.codigo}</strong>. ¿Desea proceder con un nuevo análisis clínico para este sujeto?</p>
                                    <div className="d-grid gap-2 mt-4">
                                        <button className="btn btn-success btn-lg fw-bold shadow-sm" style={{ borderRadius: '14px' }} onClick={() => { setCreatedPatient(patientExistsInProject); setShowClassification(true); setShowConfirm(false); }}>INICIAR EVALUACIÓN CLÍNICA</button>
                                        <button className="btn btn-link text-muted fw-bold" onClick={() => setShowConfirm(false)}>CANCELAR Y REVISAR</button>
                                    </div>
                                </>
                            ) : (
                                <>
                                    <h4 className="fw-bold text-dark">Validación de Registro</h4>
                                    <p className="text-muted">Se procederá a registrar a <strong>{confirmData.nombre}</strong> en el sistema central del proyecto.</p>
                                    <div className="d-grid gap-2 mt-4">
                                        <button className="btn btn-success btn-lg fw-bold shadow-sm" style={{ borderRadius: '14px' }} onClick={handleFinalSubmit}>CONFIRMAR Y VINCULAR</button>
                                        <button className="btn btn-link text-muted fw-bold" onClick={() => setShowConfirm(false)}>CORREGIR INFORMACIÓN</button>
                                    </div>
                                </>
                            )}
                        </div>
                    </div>
                </div>
            )}

            {/* Modal de Clasificación Directa (Motor Casos) */}
            {showClassification && createdPatient && (
                <div className="position-absolute top-0 start-0 w-100 h-100 d-flex align-items-center justify-content-center p-3" style={{ zIndex: 1200, background: 'rgba(255,255,255,0.9)', backdropFilter: 'blur(15px)', borderRadius: '30px' }}>
                    <div className="card border-0 shadow-lg animate__animated animate__fadeInUp" style={{ maxWidth: '400px', borderRadius: '30px' }}>
                        <div className="card-body p-5 text-center">
                            <h4 className="fw-bold text-dark mb-1">CRIBAJE RÁPIDO</h4>
                            <p className="text-muted small mb-4">Clasificación inicial del sujeto post-vinculación</p>

                            <div className="d-grid gap-3">
                                <button className="btn btn-outline-danger btn-lg fw-bold py-3" style={{ borderRadius: '16px', borderWidth: '2px' }} onClick={() => handleClassification('POSITIVO')} disabled={loadingClassification}>
                                    {loadingClassification ? <span className="spinner-border spinner-border-sm me-2"></span> : <i className="bi bi-plus-circle-fill me-2"></i>}
                                    SÍNTOMAS / POSITIVO
                                </button>
                                <button className="btn btn-outline-success btn-lg fw-bold py-3" style={{ borderRadius: '16px', borderWidth: '2px' }} onClick={() => handleClassification('NEGATIVO')} disabled={loadingClassification}>
                                    {loadingClassification ? <span className="spinner-border spinner-border-sm me-2"></span> : <i className="bi bi-dash-circle-fill me-2"></i>}
                                    ASINTOMÁTICO / NEGATIVO
                                </button>
                            </div>

                            <p className="mt-4 smaller text-muted italic">Esta clasificación generará automáticamente un caso clínico en fase de evaluación inicial.</p>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default RegistrarPaciente;

